/**
 * Cytoscape.js Types and Constants for XENDREL
 *
 * WebGL-enabled graph visualization
 */

import { needsAuthedFetch } from '@/lib/authed-image-cache'
import { isMinimalNode } from './entity-icons'

// ============================================================================
// NODE & EDGE TYPES
// ============================================================================

export interface GraphNode {
  id: string
  name: string
  labels?: string[]
  typeLabel?: string
  properties?: Record<string, unknown>
  // Computed
  displayName?: string
  hasChildren?: boolean
  expanded?: boolean
  // Collapsed children (nodes with >30 direct children are collapsed by default)
  hasCollapsedChildren?: boolean
  collapsedChildCount?: number
  // Unloaded neighbours — 1-hop neighbours that exist in the case's Neo4j
  // graph but are NOT currently in the view. Drives the "+N" expansion-hint
  // badge and the per-node "Expand…" popover. Distinct from
  // `hasCollapsedChildren` (which is for in-view compaction).
  unloadedNeighborCount?: number
  unloadedNeighborsByLabel?: Record<string, number>
  // Position (from saved layout)
  savedPosition?: { x: number; y: number }
  // Compound nodes (for visual grouping)
  parent?: string // ID of parent compound node (if this node is grouped)
  isCompound?: boolean // True if this node IS a compound parent itself
}

export interface GraphEdge {
  id: string
  type: string
  source: string
  target: string
  properties?: Record<string, unknown>
}

export interface GraphData {
  nodes: GraphNode[]
  edges: GraphEdge[]
}

// ============================================================================
// SELECTION TYPES
// ============================================================================

export interface SelectedNode {
  id: string
  name: string
  labels: string[]
  typeLabel?: string
  properties: Record<string, unknown>
}

export interface SelectedEdge {
  id: string
  type: string
  source: string
  target: string
  sourceName?: string
  targetName?: string
  properties?: Record<string, unknown>
  sourceNode?: {
    id: string
    labels: string[]
    properties: Record<string, unknown>
  }
  targetNode?: {
    id: string
    labels: string[]
    properties: Record<string, unknown>
  }
}

// ============================================================================
// FILTER TYPES
// ============================================================================

export interface GraphFilters {
  hiddenLabels: Set<string>
  hiddenWorkflows: Set<string>
  hiddenGroups: Set<string>
  highlightedWorkflow: string | null
  highlightedGroup: string | null
  searchQuery: string
}

// ============================================================================
// PATH FINDING TYPES
// ============================================================================

export interface PathResult {
  found: boolean
  path_length: number
  total_cost?: number
  nodes: Array<{ id: string; name: string; labels: string[] }>
  relationships: Array<{ id: string; type: string; source: string; target: string }>
  node_ids: string[]
  relationship_ids: string[]
  // Aliases for convenience
  nodeIds: string[]
  edgeIds: string[]
  cost: number | null
}

// ============================================================================
// LOD (Level of Detail) TYPES
// ============================================================================

export interface LODLevel {
  minZoom: number
  maxZoom: number
  showLabels: boolean
  showEdgeLabels: boolean
  showIcons: boolean
  nodeScale: number
  edgeStyle: 'bezier' | 'haystack'
}

export interface LODLevelMap {
  minimal: LODLevel
  basic: LODLevel
  standard: LODLevel
  detailed: LODLevel
  full: LODLevel
}

export const LOD_LEVELS: LODLevelMap = {
  minimal: {
    minZoom: 0,
    maxZoom: 0.13,
    showLabels: false,
    showEdgeLabels: false,
    showIcons: false,
    nodeScale: 0.3,
    edgeStyle: 'haystack',
  },
  basic: {
    minZoom: 0.13,
    maxZoom: 0.42,
    showLabels: false,
    showEdgeLabels: false,
    showIcons: false,
    nodeScale: 0.5,
    edgeStyle: 'haystack',
  },
  standard: {
    minZoom: 0.42,
    maxZoom: 0.76,
    showLabels: true,
    showEdgeLabels: true,
    showIcons: true,
    nodeScale: 1.0,
    edgeStyle: 'bezier',
  },
  detailed: {
    minZoom: 0.76,
    maxZoom: 1.6,
    showLabels: true,
    showEdgeLabels: true,
    showIcons: true,
    nodeScale: 1.0,
    edgeStyle: 'bezier',
  },
  full: {
    minZoom: 1.6,
    maxZoom: Infinity,
    showLabels: true,
    showEdgeLabels: true,
    showIcons: true,
    nodeScale: 1.0,
    edgeStyle: 'bezier',
  },
}

// ============================================================================
// COLOR PALETTE - Now using unified colors from entity-icons
// ============================================================================

import { UNIFIED_NODE_COLORS } from './entity-icons'

// Export for backward compatibility
export const SPECTRE_COLORS = {
  Default: {
    bg: UNIFIED_NODE_COLORS.bg,
    border: UNIFIED_NODE_COLORS.border,
    text: UNIFIED_NODE_COLORS.text,
  },
} as const

export type NodeTypeLabel = string

// UI Colors
export const UI_COLORS = {
  background: '#0a0a0a',
  edge: '#64748b',
  edgeLabel: '#94a3b8',
  selection: UNIFIED_NODE_COLORS.selectedBg,
  highlight: '#22c55e',
  possibleMatch: '#ef4444',
  pathHighlight: '#00ff41', // Cool neon green for path highlighting
} as const

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Synthetic compound parents generated by the Clusters (Louvain) layout are
 * not real graph entities — target-picking tools (connect, find path) and
 * hover chrome must skip them.
 */
export function isClusterNodeId(id: string): boolean {
  return id.startsWith('__cluster_')
}

export function getNodeColors(_typeLabel: string | undefined): {
  bg: string
  border: string
  text: string
} {
  // All nodes now use unified colors
  return {
    bg: UNIFIED_NODE_COLORS.bg,
    border: UNIFIED_NODE_COLORS.border,
    text: UNIFIED_NODE_COLORS.text,
  }
}

export function getDisplayName(node: GraphNode): string {
  if (node.displayName) return node.displayName
  if (node.name) return node.name

  const props = node.properties || {}
  const typeLabel = node.typeLabel || node.labels?.[0]

  // Special handling for Finding nodes - show formatted timestamp
  if (typeLabel === 'Finding') {
    const timestamp = props.finding_timestamp || props.timestamp
    if (timestamp) {
      try {
        const date = new Date(timestamp as string)
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      } catch {
        return String(timestamp)
      }
    }
    return 'Finding'
  }

  const nameKeys = [
    'display_name',
    'displayName',
    'full_name',
    'fullName',
    'title',
    'name',
    'label',
  ]

  for (const key of nameKeys) {
    const value = props[key]
    if (typeof value === 'string' && value.trim()) {
      return value.trim()
    }
  }

  return node.id.slice(0, 8)
}

export function prettifyLabel(text: string): string {
  return text
    .replace(/[_]+/g, ' ')
    .replace(/[-/]/g, ' $& ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
}

// ============================================================================
// CYTOSCAPE ELEMENT CONVERSION
// ============================================================================

export interface CytoscapeNodeData {
  id: string
  name: string
  displayName: string
  shortLabel: string // Truncated label for display
  shortDocLabel?: string // For Document nodes - title truncated to 40 chars
  typeLabel: string
  labels: string[]
  properties: Record<string, unknown>
  hasChildren: boolean
  expanded: boolean
  baseWidth: number
  baseHeight: number
  imageUrl?: string // Directly loadable image (external URL or resolved blob URL)
  authedImageUrl?: string // Auth-gated /api/ path, resolved into imageUrl via the blob cache
  icon?: string // Unicode icon character for the entity type
  isMinimal?: boolean // True for minimal nodes like Chunks
  isCompound?: boolean // True for compound nodes (containers)
  parent?: string // Parent node ID for compound node children
}

export interface CytoscapeEdgeData {
  id: string
  source: string
  target: string
  type: string
  properties: Record<string, unknown>
}

/**
 * Create a short label for display in node
 * - If > 30 chars, return empty string (don't show)
 * - Otherwise truncate to 20 chars with ellipsis
 */
function createShortLabel(name: string): string {
  if (!name) return ''
  if (name.length > 30) return '' // Too long, don't show
  if (name.length > 20) return `${name.substring(0, 18)}…`
  return name
}

/**
 * The label of a transcript passage in the graph, and so its type on the canvas
 * (`typeLabel`); the pipeline's `PASSAGE_LABEL`. Named once, here: the
 * stylesheet, the placement of an expand and the entity panel read it. Only map
 * keys and selector strings spell it out.
 */
export const PASSAGE_LABEL = 'TranscriptPassage'

/**
 * A transcript passage is drawn as a chip that holds its span, "12:30 – 13:42"
 * (the rule is in styles.ts): one line tall, and as wide as the span reads in
 * the chip's 10px mono — a span past the hour is longer and must not be cut.
 */
const PASSAGE_CHIP_HEIGHT = 22
const PASSAGE_CHIP_PADDING = 18
/** The advance of one character of the chip's label. */
const PASSAGE_CHAR_WIDTH = 6.1
/** A chip is never narrower than a lone time, nor wider than a span past the tenth hour. */
const PASSAGE_CHIP_CHARS = { min: 5, max: 19 }

function passageChipWidth(span: string): number {
  const chars = Math.min(PASSAGE_CHIP_CHARS.max, Math.max(PASSAGE_CHIP_CHARS.min, span.length))
  return Math.round(chars * PASSAGE_CHAR_WIDTH) + PASSAGE_CHIP_PADDING
}

// ============================================================================
// ELEMENT CONVERSION
// ============================================================================

export function nodeToCytoscapeElement(node: GraphNode, degree = 0) {
  const typeLabel = node.typeLabel || node.labels?.[0] || 'Default'
  const displayName = getDisplayName(node)
  const shortLabel = createShortLabel(displayName)

  // Check if this is a minimal node (like Chunk)
  const minimal = isMinimalNode(typeLabel)

  // Adjust size based on node type
  // Minimal nodes are tiny dots (6px), regular nodes scale with degree
  // Important entities (Person, Document) are larger, Companies and CorporateRoles are smaller
  let baseSize: number
  let baseWidth: number
  let baseHeight: number

  if (minimal) {
    baseSize = 6
    baseWidth = baseSize
    baseHeight = baseSize
  } else if (typeLabel === 'Note' || typeLabel === 'StickyNote') {
    // Notes are rectangular like sticky notes (wider than tall)
    baseWidth = 80
    baseHeight = 60
    baseSize = baseWidth // For compatibility
  } else if (typeLabel === 'Face') {
    // Faces are compact squares — one case can hold hundreds of crops.
    baseSize = 34
    baseWidth = baseSize
    baseHeight = baseSize
  } else if (typeLabel === PASSAGE_LABEL) {
    // A passage is a timecode chip, sized by its span and never by its degree:
    // a recording holds dozens, and they read as a ruler around it.
    baseWidth = passageChipWidth(displayName)
    baseHeight = PASSAGE_CHIP_HEIGHT
    baseSize = baseWidth // For compatibility
  } else if (typeLabel === 'Picture' || typeLabel === 'Photo') {
    // Pictures/Photos are square, slightly larger to show image clearly
    baseSize = 60
    baseWidth = baseSize
    baseHeight = baseSize
  } else if (typeLabel === 'Company' || typeLabel === 'CorporateRole') {
    // Companies and CorporateRoles are smaller (less important)
    baseSize = 25 + Math.min(degree * 1.5, 15)
    baseWidth = baseSize
    baseHeight = baseSize
  } else {
    // Important entities: Person, Document, and others
    baseSize = 35 + Math.min(degree * 2, 20)
    baseWidth = baseSize
    baseHeight = baseSize
  }

  // Extract image URL for ANY node type that has image properties
  // Documents and identification documents always show their type icon.
  let imageUrl: string | undefined
  let authedImageUrl: string | undefined
  const documentTypes = ['Document', 'AttachedDocument', 'Recording', 'IdentificationDocument']
  const isDocumentNode =
    documentTypes.includes(typeLabel) ||
    Boolean(node.labels?.some(label => documentTypes.includes(label)))

  // Document nodes should NOT have imageUrl - they always show the document icon
  if (node.properties && !isDocumentNode) {
    // Check for image properties (in priority order)
    const imgPath = String(
      node.properties.crop_path || // Face crops (ADR-0038) live on their own property
        node.properties.cropPath ||
        node.properties.photo_path ||
        node.properties.photoPath ||
        node.properties.image_url ||
        node.properties.imageUrl ||
        node.properties.image_path ||
        node.properties.imagePath ||
        node.properties.photo ||
        node.properties.image ||
        node.properties.thumbnail ||
        node.properties.url ||
        node.properties.path ||
        ''
    )

    if (imgPath && imgPath !== 'undefined' && imgPath !== 'null') {
      // Some properties (e.g. profile page URLs) look like URLs but aren't
      // actual images. Loading them into Cytoscape causes per-node CORS errors
      // and retries. Require a known image extension for full URLs.
      const isExternalUrl = imgPath.startsWith('http://') || imgPath.startsWith('https://')
      const pathOnly = isExternalUrl ? imgPath.split('?')[0].split('#')[0] : imgPath
      const looksLikeImage = /\.(jpe?g|png|gif|webp|svg|avif|bmp|heic|heif)$/i.test(pathOnly)

      if (isExternalUrl && !looksLikeImage) {
        // Skip — not an image resource
      } else if (isExternalUrl) {
        imageUrl = imgPath
      } else if (needsAuthedFetch(imgPath)) {
        // Auth-gated API URL written by the extraction pipeline
        // (/api/v1/files/<s3_key>). Cytoscape loads `background-image` with a
        // bare request carrying no bearer token, so this path is resolved into
        // a blob URL first (see authed-node-images.ts).
        authedImageUrl = imgPath
      }
      // Anything else is a pre-v2 local-filesystem path whose static mount
      // no longer exists — leave the node without an image.
    }
  }

  const labels = node.labels || [typeLabel]
  const properties = node.properties || {}
  // Check if "Suspect" is in the labels array OR if isSuspect/is_suspect property is truthy
  const isSuspect =
    labels.includes('Suspect') || properties.isSuspect === true || properties.is_suspect === true

  // Check if crime-related from labels or property
  const isCrimeRelated =
    labels.includes('CrimeRelated') ||
    properties.isCrimeRelated === true ||
    properties.is_crime_related === true

  // Check if bridge node (set by graph_analysis._detect_cross_component_bridges)
  const isBridge = properties.is_bridge === true || properties.isBridge === true

  // Check if this is an uploaded source document (vs extracted/referenced)
  const isSource = properties.is_source === true

  // Format date label for Event nodes (only if date exists)
  let dateLabel = ''
  if (typeLabel === 'Event') {
    const dateStr =
      properties.date || properties.created_at || properties.timestamp || properties.event_date
    if (dateStr) {
      try {
        const date = new Date(dateStr as string)
        // Format as: "Jan 15, 2024"
        dateLabel = date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
      } catch {
        dateLabel = String(dateStr)
      }
    }
    // If no date, leave dateLabel empty (no label will show)
  }

  // For Document nodes, create a short label from title (first 40 chars)
  let shortDocLabel = ''
  if (typeLabel === 'Document') {
    const docTitle = String(
      properties.title || properties.name || properties.doc_id || displayName || ''
    )
    shortDocLabel = docTitle.length > 40 ? `${docTitle.substring(0, 37)}...` : docTitle
  }

  return {
    group: 'nodes' as const,
    data: {
      id: node.id,
      parent: node.parent, // For compound nodes
      name: node.name || displayName,
      displayName,
      shortLabel,
      shortDocLabel, // For Document nodes - title truncated to 40 chars
      typeLabel,
      labels,
      isSuspect: isSuspect, // Will be true or false
      isCrimeRelated: isCrimeRelated, // Will be true or false
      isBridge: isBridge, // Cross-component bridge node — orange glow
      isSource: isSource, // For Document nodes - uploaded source vs extracted
      dateLabel, // For Event nodes to show date below
      properties,
      hasChildren: node.hasChildren || false,
      expanded: node.expanded || false,
      // Collapsed children info - from backend
      hasCollapsedChildren: node.hasCollapsedChildren || false,
      collapsedChildCount: node.collapsedChildCount || 0,
      baseWidth,
      baseHeight,
      imageUrl,
      authedImageUrl,
      isMinimal: minimal,
      isCompound: node.isCompound, // For compound nodes (containers)
    },
    position: node.savedPosition || undefined,
  }
}

export function edgeToCytoscapeElement(edge: GraphEdge) {
  const props = edge.properties || {}

  return {
    group: 'edges' as const,
    data: {
      id: edge.id,
      source: edge.source,
      target: edge.target,
      type: edge.type,
      properties: props,
      // Aggregated edge properties - used for collapsed nodes and DailySummary connections
      aggregated: props.aggregated || false,
      originalType: props.originalType || edge.type,
      childCount: props.childCount || props.eventCount || 0,
      collapsedParentId: props.collapsedParentId || props.dailySummaryId || null,
    },
  }
}

export function graphDataToCytoscapeElements(data: GraphData) {
  // Calculate degrees
  const degreeMap = new Map<string, number>()
  data.edges.forEach(edge => {
    degreeMap.set(edge.source, (degreeMap.get(edge.source) || 0) + 1)
    degreeMap.set(edge.target, (degreeMap.get(edge.target) || 0) + 1)
  })

  // Separate compound nodes (parents) from regular nodes
  // Cytoscape requires parent nodes to be added BEFORE their children
  const compoundNodes = data.nodes.filter(n => n.isCompound)
  const regularNodes = data.nodes.filter(n => !n.isCompound)

  // Drop edges whose endpoint node is absent: cytoscape throws on them, and
  // when this output seeds `cytoscape({ elements })` that throw happens during
  // construction — a permanently blank canvas (see safe-batch.ts for the
  // incremental-add counterpart).
  const nodeIds = new Set(data.nodes.map(n => n.id))
  const edges = data.edges.filter(e => nodeIds.has(e.source) && nodeIds.has(e.target))
  if (edges.length < data.edges.length) {
    console.warn(
      `[Cytoscape] Dropped ${data.edges.length - edges.length} edge(s) with missing endpoints`
    )
  }

  return [
    // Add compound nodes FIRST (must be before children in Cytoscape)
    ...compoundNodes.map(node => nodeToCytoscapeElement(node, 0)),
    // Then add regular nodes
    ...regularNodes.map(node => nodeToCytoscapeElement(node, degreeMap.get(node.id) || 0)),
    // Finally add edges
    ...edges.map(edge => edgeToCytoscapeElement(edge)),
  ]
}
